import { Autocomplete, Box, Button, Checkbox, FormControlLabel, FormGroup, Grid, InputLabel, TextField, ThemeProvider, Typography, Select, SelectChangeEvent, SxProps, Tooltip, createTheme, ButtonGroup, TextFieldProps, FormControlLabelProps, SelectProps, SvgIconTypeMap } from "@mui/material";
import BaseModel from "../models/_BaseModel";
import { forwardRef, RefObject, SyntheticEvent, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { DataFieldInputType, DataFormDetailType, DataFormFieldChangeParamType, DataFormFieldType, DataFormType, DropzoneType, FormInputHandle, FunctionObjectType, FunctionObjectWithReturnValueType, SaveDataParamType } from "../utils/Types";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, DatePickerProps, DateTimePicker, DateTimePickerProps, LocalizationProvider } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import '../styles/all.css';
import { DATETIME_FORMAT_DEFAULT, DATE_FORMAT_DISPLAY_DEFAULT, PageContainerActionParamType, buttonTheme, dateToString, getDecimalDisplay, getIntegerDisplay, getPathObject, separateBase64String } from "../utils/Common";
import { useStateContext } from "../reducers";
import Dropzone from "./Dropzone";
import {
    Save as SaveIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { ButtonFormTheme, comboboxInputStyle, commonInputStyle, commonStyle, dateInputStyle, GridColorStyles, textareaInputStyle, textInputStyle } from "../expanded-theme";
import FileSaver from "file-saver";
import CustomNumberInput from "./form-inputs/CustomNumberInput";
import { NumberInputProps } from "@mui/base";
import MultilineInput from "./form-inputs/MultilineInput";
import MenuItem from '@mui/material/MenuItem';
import { deepmerge } from "@mui/utils";

function DataFieldInputComponent<T extends BaseModel>(props: DataFieldInputType<T>, ref: React.Ref<FormInputHandle<T>>) {
    const {
        id,
        data,
        fields,
        defaultFocusedField,
        apiCaller,
    } = props;
    
    const [comboboxLoading, setComboboxLoading] = useState<boolean>(false);
    const [itemInEdit, setItemInEdit] = useState<T>(data);
    const [currentFocusedField, setCurrentFocusedField] = useState<string | null | undefined>(defaultFocusedField);
    const [canSave, setCanSave] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
        getData: () => itemInEdit,
        validationResult: canSave,
        currentFocusedField,
    }))

    useEffect(() => {
        setItemInEdit({...data});
    }, [data]);

    useEffect(() => {
        let currentCanSave = true;

        for (let i = 0; i < fields.length && currentCanSave; i++) {
            const field = fields[i];

            if (!field.isRequired)
                continue;

            if (field.inputType === "combobox" || field.inputType === "dropdown")
            {
                currentCanSave = !!(itemInEdit as any)[(field.editKeyField ?? "")];
                continue;
            }

            if (field.inputType === "integer" || field.inputType === "float")
            {
                currentCanSave = (itemInEdit as any)[(field.name)] > 0 || (itemInEdit as any)[(field.name)] < 0;
                continue;
            }

            currentCanSave = !!(itemInEdit as any)[field.name];
        }

        setCanSave(currentCanSave);
    }, [currentFocusedField]);

    const onLoadComboboxData = useCallback((
		id_field_name: string,
        label_field_name: string,
		url: string | undefined,
        searchEndpointPathParamName: string[] | null = null,
		filterParamName: string | null = null,
		filter: string | null = null,
		ref: RefObject<any> | null = null,
		firstLoad: boolean = false
    ) => {
        if (!url)
            return;
        
        if (!firstLoad)
            setComboboxLoading(true);

        if (searchEndpointPathParamName?.length)
        {
            const paramsInPath = searchEndpointPathParamName
                .map(p => {
                    // console.log({p, itemInEdit, value: (itemInEdit as any)[p]});
                    return (itemInEdit as any)[p];
                })
                .filter(p => !!p);

            if (paramsInPath.length)
                url = `${url}/${paramsInPath.join("/")}`;

            // console.log({paramsInPath, url});
        }

        apiCaller?.get(`${url}${filter ? `?${filterParamName}=${filter}` : ""}`)
            .then((response) => {
				// console.log("▶RESPONSE: " + response.data.data);
                if (ref?.current)
                    ref.current.options = response.data.data.map((d: any) => ({...d, disabled: false}));

                if (ref?.current?.options?.length && response.data.total > ref?.current?.options?.length)
                    ref.current.options.splice(0, 0, {
                        [id_field_name]: null,
                        [label_field_name]: "- ketik untuk mencari data lain -",
                        disabled: true,
                    });

                if (ref?.current?.options?.length && response.data.total <= ref?.current?.options?.length)
                    ref.current.options.splice(0, 0, {
                        [id_field_name]: null,
                        [label_field_name]: "- semua data berhasil ditampilkan -",
                        disabled: true,
                    });
            })
            .catch((error) => {
                console.error({error});

                if (ref?.current)
                    ref.current.options = [];
            })
            .finally(() => {
                if (!firstLoad)
                    setComboboxLoading(false);
            })
        ;
    }, [apiCaller, itemInEdit]);

    const onChange = useCallback((valueObj: DataFormFieldChangeParamType<T>, onChangeEvent?: FunctionObjectType<DataFormFieldChangeParamType<T>> | ((rowData: DataFormFieldChangeParamType<T>) => void) | ((rowData: DataFormFieldChangeParamType<T>) => {})) => {
        const newItemInEdit =
            !valueObj.isObject ?
            {...itemInEdit, [valueObj.fieldName]: valueObj.value}
            :
            {...itemInEdit, [valueObj.name ?? ""]: valueObj.value, [valueObj.fieldName]: valueObj.value ? valueObj.value[valueObj.keyFieldName ?? ""] : null};

        setItemInEdit(newItemInEdit);

        console.log({itemInEdit, valueObj, newItemInEdit});

        if (onChangeEvent)
            onChangeEvent({...valueObj, data: newItemInEdit});
    }, [itemInEdit]);

    const onSingleFileRemoved = useCallback((keyField: string, fileNameField: string, fileTypeField: string, keyFieldName: string, keyFieldValue: any, onChangeEvent?: FunctionObjectType<DataFormFieldChangeParamType<T>> | ((rowData: DataFormFieldChangeParamType<T>) => void) | ((rowData: DataFormFieldChangeParamType<T>) => {})) => {
        if (!Object.hasOwn(itemInEdit, keyField) || !Object.hasOwn(itemInEdit, fileTypeField))
            return;

        const newItemInEdit = {...itemInEdit, [keyField]: null, [fileTypeField]: null};
        setItemInEdit(newItemInEdit);

        if (onChangeEvent)
            onChangeEvent({fieldName: keyField, value: null, isFile: true, data: newItemInEdit, [keyFieldName]: keyFieldValue});
    }, [itemInEdit, currentFocusedField]);

    const onMultiFileRemoved = useCallback((idx: number, fileListField: string, keyField: string, fileNameField: string, fileTypeField: string, keyFieldName: string, keyFieldValue: any, onChangeEvent?: FunctionObjectType<DataFormFieldChangeParamType<T>> | ((rowData: DataFormFieldChangeParamType<T>) => void) | ((rowData: DataFormFieldChangeParamType<T>) => {})) => {
        if (!Object.hasOwn(itemInEdit, fileListField))
            return;

        const newFileList = (itemInEdit as any)[fileListField];
        newFileList.splice(idx, 1);
        const newItemInEdit = {...itemInEdit, [fileListField]: newFileList};
        setItemInEdit(newItemInEdit);

        if (onChangeEvent)
            onChangeEvent({fieldName: keyField, value: null, isFile: true, data: newItemInEdit, [keyFieldName]: keyFieldValue});
    }, [itemInEdit, currentFocusedField]);

    const onSingleFileUploaded = useCallback((str: string, path: string, keyField: string, fileNameField: string, fileTypeField: string, keyFieldName: string, keyFieldValue: any, onChangeEvent?: FunctionObjectType<DataFormFieldChangeParamType<T>>) => {
        if (!keyField || !fileNameField || !fileTypeField)
            return;

        const separationResult = separateBase64String(str);
        const newItemInEdit = {...itemInEdit, [keyField]: separationResult.base64Str, [fileNameField]: path, [fileTypeField]: separationResult.mimeType, [keyFieldName]: keyFieldValue};
        setItemInEdit(newItemInEdit);

        if (onChangeEvent)
            onChangeEvent({fieldName: keyField, value: null, isFile: true, data: newItemInEdit, [keyFieldName]: keyFieldValue});
    }, [itemInEdit, currentFocusedField]);

    const onMultiFileUploaded = useCallback((str: string, path: string, fileListField: string, keyField: string, fileNameField: string, fileTypeField: string, keyFieldName: string, keyFieldValue: any, onChangeEvent?: FunctionObjectType<DataFormFieldChangeParamType<T>>) => {
        if (!fileListField || !keyField || !fileNameField || !fileTypeField)
            return;

        const separationResult = separateBase64String(str);
        const newList = !Object.hasOwn(itemInEdit, fileListField) ? [] : ((itemInEdit as any)[fileListField] as any[]);
        newList.push({
            [keyField]: separationResult.base64Str,
            [fileNameField]: path,
            [fileTypeField]: separationResult.mimeType,
            [keyFieldName]: keyFieldValue,
        });
        const newItemInEdit = {...itemInEdit, [fileListField]: newList};
        setItemInEdit(newItemInEdit);

        if (onChangeEvent)
            onChangeEvent({fieldName: keyField, value: null, isFile: true, data: newItemInEdit, [keyFieldName]: keyFieldValue});
    }, [itemInEdit, currentFocusedField]);

    const onDownloadFile = useCallback((base64Str: string, filename: string, fileType: string) => {
        // const splittedFilenames = filename.split(".");
        const downloadedStr = base64Str.replace("\\", "");

        const byteCharacters = atob(downloadedStr);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const finalExportedData = new Blob([byteArray], {type: fileType});
        FileSaver.saveAs(finalExportedData, `${filename}`);
    }, []);

    const comboboxProps = useMemo(() => {
        return fields.filter(f => f.inputType === "combobox" && f.keyField).map((r: DataFormFieldType<T>) => {
            return {
                    id: r.name,
                    size: "small",
                    fullWidth: true,
                    filter: (searchText: string, key: string) => true,
                    renderInput: (params: any) => <TextField {...params} sx={{backgroundColor: "#fff"}} />,
                    options: r.ref?.current?.options ?? r.options ?? [],
                    onInputChange: (event: SyntheticEvent, value: string, reason: string) => onLoadComboboxData(r.keyField ?? "ucode", r.labelField ?? "nama", r.searchEndpoint, r.searchEndpointPathParamName, r.searchEndpointFilterParamName, value, r.ref),
                    getOptionKey: (option: any) => option[r.keyField ?? "ucode"],
                    getOptionLabel: (option: any) => option[r.labelField ?? "nama"],
                    onChange: (event: React.SyntheticEvent, value: any, reason: string, details?: string) => onChange(({value: value, name: r.name, fieldName: r.editKeyField, keyFieldName: r.keyField ?? "ucode", keyFieldValue: value ? value[r.keyField ?? "ucode"] : null, isObject: true} as any), r.onChanged),
                    onFocus: () => setCurrentFocusedField(r.name),
                    onBlur: () => setCurrentFocusedField(null),
                    autoFocus: currentFocusedField === r.name,
                    value: r.name && r.editKeyField && (itemInEdit as any)[r.editKeyField] ? (itemInEdit as any)[r.name] : null,
                    defaultValue: r.name && r.editKeyField && (itemInEdit as any)[r.editKeyField] ? (itemInEdit as any)[r.name] : null,
                    onOpen: (event: SyntheticEvent) => onLoadComboboxData(
                        r.keyField ?? "ucode",
                        r.labelField ?? "nama",
                        r.searchEndpoint,
                        r.searchEndpointPathParamName,
                        r.searchEndpointFilterParamName,
                        (r.name && r.labelField && (itemInEdit as any)[r.name]?.[r.labelField]) || "",
                        r.ref,
                    ),
                    ref: r.ref,
                    loading: comboboxLoading,
                    isOptionEqualToValue: (option: T, value: T) => r.keyField && option && (option as any)[r.keyField] ? ((option as any)[r.keyField] === ((value as any) ?? {})[r.keyField]) : false,
                    getOptionDisabled: (option: any) => option.disabled,
                }
            ;
        });
    }, [fields, itemInEdit, onLoadComboboxData, onChange, comboboxLoading, currentFocusedField]);

    const multiSelectProps = useMemo(() => {
        return fields.filter(f => f.inputType === "multiselect").map((r: DataFormFieldType<T>) => {
            return {
                    id: r.name,
                    size: "small",
                    fullWidth: true,
                    filter: (searchText: string, key: string) => true,
                    renderInput: (params: any) => <TextField {...params} sx={{backgroundColor: "#fff"}} />,
                    multiple: true,
                    options: r.ref?.current?.options ?? r.options ?? [],
                    onInputChange: (event: SyntheticEvent, value: string, reason: string) => onLoadComboboxData(r.keyField ?? "ucode", r.labelField ?? "nama", r.searchEndpoint, r.searchEndpointPathParamName, r.searchEndpointFilterParamName, value, r.ref),
                    getOptionKey: (option: any) => option[r.keyField ?? "ucode"],
                    getOptionLabel: (option: any) => option[r.labelField ?? "nama"],
                    onChange: (event: React.SyntheticEvent, value: any, reason: string, details?: string) => onChange(({value: value, name: r.name, fieldName: r.editKeyField, keyFieldName: r.keyField ?? "ucode", keyFieldValue: value ? value[r.keyField ?? "ucode"] : null, isObject: true} as any), r.onChanged),
                    onFocus: () => setCurrentFocusedField(r.name),
                    onBlur: () => setCurrentFocusedField(null),
                    autoFocus: currentFocusedField === r.name,
                    value: r.name && r.editKeyField && (itemInEdit as any)[r.editKeyField] ? (itemInEdit as any)[r.name] : [],
                    defaultValue: r.name && r.editKeyField && (itemInEdit as any)[r.editKeyField] ? (itemInEdit as any)[r.name] : [],
                    onOpen: (event: SyntheticEvent) => onLoadComboboxData(
                        r.keyField ?? "ucode",
                        r.labelField ?? "nama",
                        r.searchEndpoint,
                        r.searchEndpointPathParamName,
                        r.searchEndpointFilterParamName,
                        (r.name && r.labelField && (itemInEdit as any)[r.name]?.[r.labelField]) || "",
                        r.ref,
                    ),
                    ref: r.ref,
                    loading: comboboxLoading,
                    getOptionDisabled: (option: any) => option.disabled,
                }
            ;
        });
    }, [fields, itemInEdit, onLoadComboboxData, onChange, comboboxLoading, currentFocusedField]);

    return (
        <>
        {
            fields.map((f, idx) => {
                const value = (itemInEdit as any)[f.name];
                const latestIndex = (value ?? "").length;

                const labelProps: any = {
                    required: f.isRequired,
                    sx: {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                    }
                };

                const inputTextProps: TextFieldProps = {
                    size: "small",
                    value: value ?? "",
                    onChange: (e: any) => onChange({fieldName: f.name, value: e.target.value, keyFieldName: f.keyField ?? "ucode", keyFieldValue: (itemInEdit as any)[f.keyField ?? "ucode"]}, f.onChanged),
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                        e.target.setSelectionRange(latestIndex, latestIndex);
                        setCurrentFocusedField(f.name);
                    },
                    onBlur: () => setCurrentFocusedField(null),
                    autoFocus: currentFocusedField === f.name,
                    required: f.isRequired,
                    autoComplete: "off",
                }
                ;

                const inputIntegerProps: NumberInputProps = {
                    value: value ?? "",
                    onChange: (e: any, value) => onChange({fieldName: f.name, value, keyFieldName: f.keyField ?? "ucode", keyFieldValue: (itemInEdit as any)[f.keyField ?? "ucode"]}, f.onChanged),
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                        e.target.setSelectionRange(latestIndex, latestIndex);
                        setCurrentFocusedField(f.name);
                    },
                    onBlur: () => setCurrentFocusedField(null),
                    autoFocus: currentFocusedField === f.name,
                    required: f.isRequired,
                    min: f.minValue,
                    max: f.maxValue,
                }
                ;

                const inputFloatProps: NumberInputProps & { isDecimal?: boolean | true } = {
                    value: value ?? "",
                    onChange: (e: any, value) => onChange({fieldName: f.name, value, keyFieldName: f.keyField ?? "ucode", keyFieldValue: (itemInEdit as any)[f.keyField ?? "ucode"]}, f.onChanged),
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                        e.target.setSelectionRange(latestIndex, latestIndex);
                        setCurrentFocusedField(f.name);
                    },
                    onBlur: () => setCurrentFocusedField(null),
                    autoFocus: currentFocusedField === f.name,
                    required: f.isRequired,
                    min: f.minValue,
                    max: f.maxValue,
                    isDecimal: true,
                }
                ;

                const inputTextareaProps: TextFieldProps = {
                    size: "small",
                    value: value ?? "",
                    multiline: true,
                    rows: f.rows ?? 3,
                    maxRows: 6,
                    onChange: (e: any) => onChange({fieldName: f.name, value: e.target.value, keyFieldName: f.keyField ?? "ucode", keyFieldValue: (itemInEdit as any)[f.keyField ?? "ucode"]}, f.onChanged),
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                        e.target.setSelectionRange(latestIndex, latestIndex);
                        setCurrentFocusedField(f.name);
                    },
                    onBlur: () => setCurrentFocusedField(null),
                    autoFocus: currentFocusedField === f.name,
                    required: f.isRequired,
                    autoComplete: "off"
                }
                ;

                const inputCheckboxProps: FormControlLabelProps = {
                    required: f.isRequired,
                    checked: value ? true : false,
                    control: currentFocusedField === f.name ?
                        <Checkbox
                            size="small"
                            checked={value ? true : false}
                            onChange={(e: any) => onChange({fieldName: f.name, value: e.target.checked, keyFieldName: f.keyField ?? "ucode", keyFieldValue: (itemInEdit as any)[f.keyField ?? "ucode"]}, f.onChanged)}
                            onFocus={() => setCurrentFocusedField(f.name)}
                            onBlur={() => setCurrentFocusedField(null)}
                            autoFocus
                        />
                        :
                        <Checkbox
                            size="small"
                            checked={value ? true : false}
                            onChange={(e: any) => onChange({fieldName: f.name, value: e.target.checked, keyFieldName: f.keyField ?? "ucode", keyFieldValue: (itemInEdit as any)[f.keyField ?? "ucode"]}, f.onChanged)}
                            onFocus={() => setCurrentFocusedField(f.name)}
                            onBlur={() => setCurrentFocusedField(null)}
                        />,
                    label: f.title,
                }

                const inputDateProps: DatePickerProps<Moment, Moment> = {
                    value: value ? moment(value) : null,
                    onChange: (changeValue: any) => onChange({fieldName: f.name, value: changeValue ? dateToString(changeValue) : null, keyFieldName: f.keyField ?? "ucode", keyFieldValue: (itemInEdit as any)[f.keyField ?? "ucode"]}),
                    autoFocus: currentFocusedField === f.name,
                    renderInput: (params: any) => <TextField {...params} />,
                    inputFormat: DATE_FORMAT_DISPLAY_DEFAULT,
                    InputProps: {
                        size: "small",
                        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                            e.target.setSelectionRange(latestIndex, latestIndex);
                            setCurrentFocusedField(f.name);
                        },
                        onBlur: () => setCurrentFocusedField(null),
                    },
                }
                ;

                const inputDateTimeProps: DateTimePickerProps<Moment, Moment> = {
                    value: value ? moment(value) : null,
                    onChange: (changeValue: any) => onChange({fieldName: f.name, value: changeValue ? dateToString(changeValue, DATETIME_FORMAT_DEFAULT) : null, keyFieldName: f.keyField ?? "ucode", keyFieldValue: (itemInEdit as any)[f.keyField ?? "ucode"]}),
                    autoFocus: currentFocusedField === f.name,
                    renderInput: (params: any) => <TextField {...params} />,
                    inputFormat: DATE_FORMAT_DISPLAY_DEFAULT,
                    InputProps: {
                        size: "small",
                        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                            e.target.setSelectionRange(latestIndex, latestIndex);
                            setCurrentFocusedField(f.name);
                        },
                        onBlur: () => setCurrentFocusedField(null),
                    },
                }
                ;

                const inputComboboxProps: any = comboboxProps.filter((c: any) => c.id === f.name)[0];

				const inputMultiSelectProps: any = multiSelectProps.filter((c: any) => c.id === f.name)[0];

                const inputDropdownProps: SelectProps<any> = {
                    size: "small",
                    value: f.dataSource && f.keyField && value && f.dataSource?.filter(d => d[f.keyField ?? ""] === value[f.keyField ?? ""])?.length ? f.dataSource?.filter(d => d[f.keyField ?? ""] === value[f.keyField ?? ""])[0][f.keyField] : null,
                    onChange: (e: any) => {
                        const valueObj = f.dataSource?.filter(d => d[f.keyField ?? ""] === e.target.value).length ? f.dataSource?.filter(d => d[f.keyField ?? ""] === e.target.value)[0] : null;
                        
                        const onChangeValueObj: DataFormFieldChangeParamType<T> = {
                            fieldName: f.editKeyField ?? "",
                            name: f.name,
                            value: valueObj,
                            keyFieldName: f.keyField ?? "ucode",
                            keyFieldValue: (itemInEdit as any)[f.keyField ?? "ucode"],
                            isObject: true,
                        };
                        onChange(onChangeValueObj, f.onChanged);
                    },
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                        setCurrentFocusedField(f.name);
                    },
                    onBlur: () => setCurrentFocusedField(null),
                    autoFocus: currentFocusedField === f.name,
                    required: f.isRequired,
                    sx: {
                        borderWidth: "0 0 2px 0 !important",
                        borderStyle: "solid",
                        borderColor: "#949494",
                        borderRadius: "0 !important",
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15);",
                        '&.Mui-focused': {
                            borderColor: "#cc1f1f",
                            borderWidth: "0 0 2px 0 !important",
                        },
                        "& fieldset": {
                            borderWidth: "0 !important",
                            height: "100%",
                            top: 0,
                        },
                    },
                }
                ;

                const inputDropzoneProps: DropzoneType = {
                    setBinaryStr: (str, path) => f.isSingleFile ? onSingleFileUploaded(str, path, f.keyField ?? "", f.fileNameField ?? "", f.fileTypeField ?? "", f.keyField ?? "ucode", (itemInEdit as any)[f.keyField ?? "ucode"]) : onMultiFileUploaded(str, path, f.fileListField ?? "", f.keyField ?? "", f.fileNameField ?? "", f.fileTypeField ?? "", f.keyField ?? "ucode", (itemInEdit as any)[f.keyField ?? "ucode"]),
                    accept: f.acceptedFileTypes,
                    onError: () => {},
                };

                return (
                    <Grid
                        item
                        xl={f.isHeader ? 12 : (f.gridSizeXl ?? (f.inputType === "file" || (f.inputType === "textarea" && !f.isReadonly) ? 12 : 3))}
                        lg={f.isHeader ? 12 : (f.gridSizeLg ?? (f.inputType === "file" || (f.inputType === "textarea" && !f.isReadonly) ? 12 : 3))}
                        md={f.isHeader ? 12 : (f.gridSizeMd ?? (f.inputType === "file" || (f.inputType === "textarea" && !f.isReadonly) ? 12 : 6))}
                        sm={f.isHeader ? 12 : (f.gridSizeSm ?? (f.inputType === "file" || (f.inputType === "textarea" && !f.isReadonly) ? 12 : 12))}
                        key={`data_input_${id}_${idx}`}
                        sx={commonStyle}
                    >
                        <FormGroup sx={{height: "100%", display: "flex", flexDirection: "column"}}>
                        {
                            f.inputType !== "checkbox" && !f.hideTitle ?
                            <Box {...labelProps}>
                                <Typography sx={{fontSize: "1rem !important", color: "rgba(0, 0, 0, 0.6)"}}>
                                {
                                    f.title
                                }
                                </Typography>
                            {
                                f.titleHasCommands && f.titleCommands?.length ?
                                <ThemeProvider theme={buttonTheme}>
                                    <ButtonGroup>
                                    {
                                        f.titleHasCommands && f.titleCommands?.map((c, title_idx) => {
                                            return (
                                                <Tooltip
                                                    title={c.title}
                                                    arrow
                                                    key={`popup_title_${c.id}_${title_idx}`}
                                                >
                                                    <Button
                                                        onClick={() => c.action()}
                                                        variant={c.variant ?? "outlined"}
                                                    >
                                                        <c.icon />
                                                    </Button>
                                                </Tooltip>
                                            );
                                        })
                                    }
                                    </ButtonGroup>
                                </ThemeProvider>
                                :
                                null
                            }
                            </Box>
                            :
                            null
                        }
                        {
                            f.isReadonly ?
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    display: "flex",
                                    justifyContent: "start",
                                    alignItems: "center",
                                }}
                            >
                                <Typography>
                                    <b>
                                    {
                                        f.inputType === "date" || f.inputType === "datetime" ?
                                        (value ? moment(value).format(DATE_FORMAT_DISPLAY_DEFAULT) : "N/A")
                                        :
                                        
                                        f.inputType === "integer" ?
                                        (value ? getIntegerDisplay(value as number) : "N/A")
                                        :
                                        
                                        f.inputType === "float" ?
                                        (value ? getDecimalDisplay(value as number) : "N/A")

                                        :
                                        value ?? "N/A"
                                    }
                                    </b>
                                </Typography>
                            </Box>
                            :
                            (
                                f.inputType === "text" ?
                                <TextField {...inputTextProps} {...commonInputStyle} {...textInputStyle}/>
                                :

                                f.inputType === "integer" ?
                                <CustomNumberInput {...inputIntegerProps}/>
                                :

                                f.inputType === "float" ?
                                <CustomNumberInput {...inputFloatProps}/>
                                :

                                f.inputType === "textarea" ?
                                <MultilineInput {...inputTextareaProps} {...commonInputStyle} {...textareaInputStyle}/>
                                :

                                f.inputType === "checkbox" ?
                                <FormControlLabel {...inputCheckboxProps} {...textInputStyle}/>
                                :

                                f.inputType === "date" ?
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker {...inputDateProps} {...dateInputStyle}/>
                                </LocalizationProvider>
                                :

                                f.inputType === "datetime" ?
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DateTimePicker {...inputDateTimeProps} {...dateInputStyle}/>
                                </LocalizationProvider>
                                :

                                f.inputType === "combobox" ?
                                <Autocomplete {...inputComboboxProps} {...commonInputStyle} {...comboboxInputStyle}/>
                                :

                                f.inputType === "dropdown" ?
                                <Select {...inputDropdownProps}>
                                {
                                    f.dataSource?.map(d => {
                                        return (
                                            <MenuItem value={d[f.keyField ?? ""]}>{d[f.labelField ?? ""]}</MenuItem>
                                        );
                                    })
                                }
                                </Select>
                                :

								f.inputType === "multiselect" ?
                                <Autocomplete {...inputMultiSelectProps} {...commonInputStyle} {...comboboxInputStyle}/>
                                : 

                                f.inputType === "file" && f.keyField ?
                                <>
                                    <Dropzone {...inputDropzoneProps} />
                                    <ThemeProvider theme={GridColorStyles}>
                                        <Grid container spacing={2} sx={{marginTop: "0.25rem"}}>
                                        {
                                            f.isSingleFile && Object.hasOwn(itemInEdit, f.keyField) && (itemInEdit as any)[f.keyField] ?
                                            <>
                                                <Grid
                                                    item
                                                    sx={{
                                                        padding: "0",
                                                        display: "flex",
                                                    }}
                                                >
                                                    <ThemeProvider theme={buttonTheme}>
                                                        <ButtonGroup className="test" sx={{marginRight: "0.5rem"}}>
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => onSingleFileRemoved(f.keyField ?? "", f.fileNameField ?? "", f.fileTypeField ?? "", f.keyField ?? "ucode", (itemInEdit as any)[f.keyField ?? "ucode"], f.onChanged)}
                                                                size="small"
                                                                color="delete"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </Button>
                                                        </ButtonGroup>
                                                    </ThemeProvider>
                                                    <Box
                                                        sx={{
                                                            height: "100%",
                                                            display: "flex",
                                                            justifyContent: "start",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Typography
                                                            onClick={() => {
                                                                if (!f.keyField || !f.fileNameField || !f.fileTypeField)
                                                                    return;

                                                                onDownloadFile((itemInEdit as any)[f.keyField], (itemInEdit as any)[f.fileNameField], (itemInEdit as any)[f.fileTypeField]);
                                                            }}
                                                            className="cursor-pointer hover-bolder"
                                                        >
                                                        {
                                                            f.fileNameField && Object.hasOwn(itemInEdit, f.fileNameField) ? (itemInEdit as any)[f.fileNameField] : (<i>[tanpa nama]</i>)
                                                        }
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </>
                                            :
                                            null
                                        }
                                        {
                                            !f.isSingleFile && f.fileListField && Object.hasOwn(itemInEdit, f.fileListField) && ((itemInEdit as any)[f.fileListField]).length ?
                                            <Grid container spacing={2} sx={{padding: "0", margin: "0"}}>
                                            {
                                                ((itemInEdit as any)[f.fileListField]).forEach((file: any, idx: number) => {
                                                    return (
                                                        <>
                                                            <Grid
                                                                item
                                                                sx={{
                                                                    padding: "0",
                                                                    display: "flex",
                                                                }}
                                                            >
                                                                <ThemeProvider theme={buttonTheme}>
                                                                    <ButtonGroup className="test">
                                                                        <Button
                                                                            variant="contained"
                                                                            onClick={() => onMultiFileRemoved(idx, f.fileListField ?? "", f.keyField ?? "", f.fileNameField ?? "", f.fileTypeField ?? "", f.keyField ?? "ucode", (itemInEdit as any)[f.keyField ?? "ucode"], f.onChanged)}
                                                                            size="small"
                                                                            color="delete"
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </Button>
                                                                    </ButtonGroup>
                                                                </ThemeProvider>
                                                                <Box
                                                                    sx={{
                                                                        height: "100%",
                                                                        display: "flex",
                                                                        justifyContent: "start",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        onClick={() => {
                                                                            if (!f.keyField || !f.fileNameField || !f.fileTypeField)
                                                                                return;
        
                                                                            onDownloadFile((file as any)[f.keyField], (file as any)[f.fileNameField], (file as any)[f.fileTypeField]);
                                                                        }}
                                                                        className="cursor-pointer hover-bolder"
                                                                    >
                                                                    {
                                                                        f.fileNameField && Object.hasOwn(file, f.fileNameField) ? (file as any)[f.fileNameField] : (<i>[tanpa nama]</i>)
                                                                    }
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        </>
                                                    );
                                                })
                                            }
                                            </Grid>
                                            :
                                            null
                                        }
                                        </Grid>
                                    </ThemeProvider>
                                </>
                                :

                                null
                            )
                        }
                        </FormGroup>
                    </Grid>
                );
            })
        }
        </>
    );
}

export const DataFieldInput = forwardRef(DataFieldInputComponent) as <T extends BaseModel>(
    props: DataFieldInputType<T> & { ref?: React.Ref<FormInputHandle<T>> }
) => ReturnType<typeof DataFieldInputComponent>;

function DataForm<T extends BaseModel>(props: DataFormType<T>) {
    const {
        groupName,
        subgroupName,
        code,
        title,
        hideDefaultCommands,
        isViewMode,
        customFormCommands,
        fields,
        data,
        onValidate,
        onClose,
        onSave,
    } = props;

    const {apiCaller} = useStateContext();
    const formRef = useRef<FormInputHandle<T>>(null);

    const pathObject: PageContainerActionParamType | null = useMemo(() => {
        return getPathObject(groupName, subgroupName, code);
    }, [groupName, subgroupName, code]);

    const handleSave = useCallback((closeAfterSave: boolean = false) => {
        const dataToSend = formRef.current?.getData();

        if (!dataToSend || !onValidate(dataToSend))
            return;

        onSave({
            data: dataToSend,
            callback: () => {
                if (!closeAfterSave)
                    return;

                onClose(pathObject);
            }
        });
    }, [formRef, onValidate, onSave, onClose, pathObject]);

    const boxInputStyle = useMemo(() => {
        return {display: "flex", justifyContent: "center", width: "100%", marginTop: "0.75rem"};
    }, []);

    const boxToolbarStyle = useMemo(() => {
        return {display: "flex", justifyContent: "start", width: "100%", marginTop: "0.75rem"};
    }, []);

    const commandButtons = useMemo(() => {
        const result = [];

        if (!hideDefaultCommands && !isViewMode)
            result.push((
                <Tooltip
                    title="Simpan"
                    arrow
                >
                    <Button
                        variant="contained"
                        onClick={() => handleSave(true)}
                    >
                        <SaveIcon fontSize="small"/>
                    </Button>
                </Tooltip>
            ));

        if (!isViewMode && customFormCommands?.filter(c => !c.disabled)?.length)
            customFormCommands.filter(c => !c.disabled).forEach((c, idx) => {
                result.push((
                    <Tooltip
                        key={`form_command_${idx}`}
                        title={c.title}
                        arrow
                    >
                        <Button
                            variant={c.variant ?? "contained"}
                            onClick={() => c.action(formRef?.current?.getData())}
                            color={c.color ?? "dark"}
                            sx={{marginLeft: `${(idx || (!hideDefaultCommands && !isViewMode)) ? "2px" : "0"} !important`}}
                        >
                        {
                            <c.icon fontSize="small" />
                        }
                        </Button>
                    </Tooltip>
                ));
            });
        
        result.push((
            <Tooltip
                title="Kembali"
                arrow
            >
                <Button
                    variant="contained"
                    onClick={() => onClose(pathObject)}
                    color="dark"
                    sx={{marginLeft: "2px !important"}}
                >
                    <ArrowBackIcon fontSize="small"/>
                </Button>
            </Tooltip>
        ));

        return result;
    }, [customFormCommands, hideDefaultCommands, isViewMode]);

    return (
        <>
            <Box sx={{...boxToolbarStyle}}>
                <ThemeProvider theme={buttonTheme}>
                    <ButtonGroup className="test">
                    {
                        commandButtons
                    }
                    </ButtonGroup>
                </ThemeProvider>
            </Box>
            <Box sx={{...boxInputStyle}}>
                <Grid container spacing={2}>
                    <DataFieldInput
                        id="header"
                        fields={fields}
                        data={data}
                        apiCaller={apiCaller}
                        ref={formRef}
                    />
                </Grid>
            </Box>
        </>
    );
}

export function DataFormDetail<T extends BaseModel>(props: DataFormDetailType<T>) {
    const {
        index,
        fields,
        data,
    } = props;

    const {apiCaller} = useStateContext();
    const formRef = useRef<FormInputHandle<T>>(null);

    const boxInputStyle = useMemo(() => {
        return {display: "flex", justifyContent: "center", width: "100%", padding: "0.5rem", marginTop: "0.25rem", marginBottom: "0.7rem", borderRadius: "0.4rem", boxShadow: "0.25rem 0.4rem 0.4rem rgba(200, 200, 200, 0.5)"};
    }, []);

    return (
        <>
            <Box sx={{...boxInputStyle}}>
                <Grid container spacing={2}>
                    <DataFieldInput
                        id={`detail_${index ?? 0}`}
                        fields={fields}
                        data={data}
                        apiCaller={apiCaller}
                        ref={formRef}
                    />
                </Grid>
            </Box>
        </>
    );
}

export default DataForm;
